import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Avatar,
  Select,
  MenuItem,
  IconButton,
  Menu,
  MenuItem as MenuItemComponent,
  Tooltip,
} from "@mui/material";
import {
  DataGridPro,
  GridColDef,
  GridRenderCellParams,
} from "@mui/x-data-grid-pro";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonAddOutlined from "@mui/icons-material/PersonAddOutlined";
import { useCustomLocaleText } from "@/style";
import { colorPalette, typeToIconMapping } from "../utils";

interface TaskListProps {
  tasks: any[];
  selectedProject: any;
  onUpdateTask: (taskId: string, updates: any) => void;
  onDeleteTask: (taskId: string) => void;
  isReadOnly?: boolean;
  showDependencyStatus?: boolean; // Add flag to show dependency status in read-only mode
}

const TaskList: React.FC<TaskListProps> = ({
  tasks,
  selectedProject,
  onUpdateTask,
  onDeleteTask,
  isReadOnly = false,
  showDependencyStatus = false,
}) => {
  const { t } = useTranslation(["bugherd"]);
  const customLocaleText = useCustomLocaleText();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);

  const getDependencyStatusColor = (status: string) => {
    switch (status) {
      case "Waiting On":
        return {
          backgroundColor: "#fbbf24", // Yellow
          color: "white",
        };
      case "Blocking":
        return {
          backgroundColor: "#ef4444", // Red
          color: "white",
        };
      case "Linked":
        return {
          backgroundColor: "#3b82f6", // Blue
          color: "white",
        };
      default:
        return {
          backgroundColor: "#6b7280", // Gray
          color: "white",
        };
    }
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    taskId: string
  ) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedTaskId(taskId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTaskId(null);
  };

  const handleDeleteClick = () => {
    if (selectedTaskId) {
      onDeleteTask(selectedTaskId);
    }
    handleMenuClose();
  };

  const getInitials = (name: string) => {
    return name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  // Filter tasks by selected project
  const filteredTasks = useMemo(() => {
    // If no selectedProject is provided (like in My Consumers), show all tasks
    if (!selectedProject) {
      return tasks;
    }

    return tasks.filter((task) => {
      return (
        task.project?._id === selectedProject._id ||
        task.projectId === selectedProject._id
      );
    });
  }, [tasks, selectedProject]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "summary",
        headerName: t("Name"),
        flex: 1,
        sortable: true,
        resizable: false,
        disableColumnMenu: true,
        renderCell: (params: GridRenderCellParams) => {
          const task = params.row;
          const type = task?.type || "TASK";
          const typeInfo =
            typeToIconMapping[type.toUpperCase()] || typeToIconMapping.TASK;

          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                width: "100%",
                height: "100%",
                overflow: "hidden",
              }}
            >
              {/* Type Icon */}
              <Box
                component="span"
                sx={{
                  width: 16,
                  height: 16,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: typeInfo?.bgcolor,
                  borderRadius: "3px",
                  color: "white",
                  flexShrink: 0,
                }}
              >
                {typeInfo?.icon}
              </Box>

              {/* Task Key */}
              {task?.bugKey && (
                <Typography
                  component="span"
                  sx={{
                    color: "#1976D2",
                    fontWeight: 500,
                    fontSize: "0.875rem",
                    flexShrink: 0,
                  }}
                >
                  {task.bugKey}:
                </Typography>
              )}

              {/* Task Summary */}
              <Typography
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  minWidth: 0,
                }}
                title={task.summary}
              >
                {task.summary}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: "assignTo",
        headerName: t("Assignee"),
        flex: 0.4,
        sortable: true,
        resizable: false,
        disableColumnMenu: true,
        renderCell: (params: GridRenderCellParams) => {
          const assignees = params.row.assignTo || [];

          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                width: "100%",
                overflow: "hidden",
              }}
            >
              <Box
                display="flex"
                justifyContent="flex-start"
                alignItems="center"
              >
                {assignees.length === 0 && (
                  <PersonAddOutlined
                    sx={{ fontSize: 16, color: "text.secondary" }}
                  />
                )}
                {assignees.slice(0, 3).map((item: any, index: number) => (
                  <Tooltip key={item._id} title={item.fullName} arrow>
                    <Avatar
                      sx={{
                        backgroundColor:
                          colorPalette[index % colorPalette.length],
                        marginRight: "-4px",
                        cursor: "pointer",
                        position: "relative",
                        zIndex: 10 - index,
                        width: "25px",
                        height: "25px",
                        fontSize: "10px",
                        fontWeight: 500,
                        border: "1px solid white",
                      }}
                    >
                      {getInitials(item?.fullName)}
                    </Avatar>
                  </Tooltip>
                ))}

                {assignees.length > 3 && (
                  <Avatar
                    sx={{
                      backgroundColor: "#ccc",
                      marginRight: 1,
                      cursor: "pointer",
                      width: "25px",
                      height: "25px",
                      fontSize: "10px",
                      border: "1px solid #fff",
                      fontWeight: 500,
                    }}
                  >
                    +{assignees.length - 3}
                  </Avatar>
                )}
              </Box>
            </Box>
          );
        },
        headerAlign: "left",
      },
      // Add Project column for My Consumers view
      ...(selectedProject ? [] : [{
        field: "project",
        headerName: t("Project"),
        flex: 0.4,
        sortable: true,
        resizable: false,
        disableColumnMenu: true,
        renderCell: (params: GridRenderCellParams) => {
          const task = params.row;
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                width: "100%",
                overflow: "hidden",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.875rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {task?.project?.name || "Unknown Project"}
              </Typography>
            </Box>
          );
        },
        headerAlign: "left",
      }]),
      {
        field: "taskStatus",
        headerName: t("Task Status"),
        flex: 0.5,
        sortable: true,
        resizable: false,
        disableColumnMenu: true,
        renderCell: (params: GridRenderCellParams) => {
          const task = params.row;
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                width: "100%",
                overflow: "hidden",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.875rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {task?.bugStatus?.status || "To Do"}
              </Typography>
            </Box>
          );
        },
        headerAlign: "left",
      },
      // Show dependency status column based on conditions
      ...((isReadOnly && showDependencyStatus) || (!isReadOnly) ? [{
        field: "dependencyStatus",
        headerName: t("Dependency Type"),
        flex: 0.5,
        sortable: true,
        resizable: false,
        disableColumnMenu: true,
        renderCell: (params: GridRenderCellParams) => {
          const task = params.row;
          
          if (isReadOnly && showDependencyStatus) {
            // Read-only mode: show colored chip
            const statusColors = getDependencyStatusColor(task.status);
            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                  width: "100%",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.875rem",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    color: statusColors.backgroundColor,
                    fontWeight: 500,
                  }}
                >
                  {task.status}
                </Typography>
              </Box>
            );
          } else {
            // Editable mode: show dropdown
            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                  width: "100%",
                }}
              >
                <Select
                  value={task.status}
                  size="small"
                  onChange={(e) => {
                    e.stopPropagation();
                    onUpdateTask(task._id, { status: e.target.value });
                  }}
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    width: "100%",
                    "& .MuiSelect-select": {
                      py: 0.5,
                      fontSize: "0.875rem",
                    },
                  }}
                >
                  <MenuItem value="Waiting On">{t("Waiting On")}</MenuItem>
                  <MenuItem value="Blocking">{t("Blocking")}</MenuItem>
                  <MenuItem value="Linked">{t("Linked")}</MenuItem>
                </Select>
              </Box>
            );
          }
        },
        headerAlign: "left",
      }] : []),
      // Only show actions column if not read-only
      ...(isReadOnly ? [] : [{
        field: "actions",
        headerName: "",
        width: 60,
        sortable: false,
        filterable: false,
        resizable: false,
        disableColumnMenu: true,
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                width: "100%",
              }}
            >
              <IconButton
                size="small"
                onClick={(e) => handleMenuOpen(e, params.row._id)}
              >
                <MoreVertIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          );
        },
      }]),
    ],
    [t, onUpdateTask, colorPalette, selectedProject, isReadOnly, showDependencyStatus]
  );


  // Prepare rows for DataGrid
  const rows = useMemo(() => {
    return filteredTasks.map((task) => ({
      id: task._id,
      ...task,
    }));
  }, [filteredTasks]);

  if (!selectedProject && filteredTasks.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "200px",
          color: "#666",
        }}
      >
        <Typography variant="body2" sx={{ fontSize: "14px" }}>{t("No consumer tasks found")}</Typography>
      </Box>
    );
  }

  if (selectedProject && !selectedProject) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "200px",
          color: "#666",
        }}
      >
        <Typography variant="body2" sx={{ fontSize: "14px" }}>
          {t("Select a project to view tasks")}
        </Typography>
      </Box>
    );
  }

  if (selectedProject && filteredTasks.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "200px",
          color: "#666",
        }}
      >
        <Typography variant="body2" sx={{ fontSize: "14px" }}>
          {t("No tasks added for")} {selectedProject.name}.{" "}
          {t("Use the search bar above to add tasks.")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* DataGrid Container */}
      <Box
        sx={{
          flex: 1,
          width: "100%",
          minHeight: 0,
          position: "relative",
        }}
      >
        <DataGridPro
          rows={rows}
          columns={columns}
          localeText={customLocaleText}
          disableRowSelectionOnClick
          rowSelection={false}
          pageSizeOptions={[10, 25, 50, 100]}
          getRowHeight={() => 50}
          sortingOrder={["asc", "desc"]}
          sx={{
            height: "100%",
            width: "100%",
            px: 2,
          }}
        />
      </Box>

      {/* Context Menu */}
      {!isReadOnly && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          <MenuItemComponent onClick={handleDeleteClick}>
            <DeleteIcon sx={{ fontSize: 16, mr: 1 }} />
            {t("Remove")}
          </MenuItemComponent>
        </Menu>
      )}
    </Box>
  );
};

export default TaskList;
